import { supabase } from './supabase';
import { stripeApi } from './stripeApi';
import { getStripe } from './stripe';

export interface CreateSubscriptionParams {
  planId: string;
  userId: string;
  planName: string;
  billingCycle: 'monthly' | 'annual';
  price: number;
}

export interface SubscriptionResponse {
  success: boolean;
  subscription?: any;
  error?: string;
}

export const subscriptionApi = {
  // Create a new subscription with Stripe
  async createSubscription(params: CreateSubscriptionParams): Promise<SubscriptionResponse> {
    try {
      console.log('Starting subscription creation with params:', params);
      
      // Get plan details
      const { data: plan, error: planError } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('id', params.planId)
        .single();

      if (planError) {
        console.error('Plan error:', planError);
        throw new Error('Plan not found');
      }

      console.log('Plan found:', plan);

      // Create Stripe checkout session
      console.log('Creating Stripe checkout session...');
      const checkoutResult = await stripeApi.createCheckoutSession({
        planId: params.planId,
        userId: params.userId,
        planName: params.planName,
        billingCycle: params.billingCycle,
        price: params.price,
      });

      console.log('Checkout result:', checkoutResult);

      if (!checkoutResult.success) {
        throw new Error(checkoutResult.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe checkout
      console.log('Loading Stripe...');
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      console.log('Redirecting to Stripe checkout with session ID:', checkoutResult.data.sessionId);
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: checkoutResult.data.sessionId,
      });

      if (stripeError) {
        console.error('Stripe redirect error:', stripeError);
        throw stripeError;
      }

      console.log('Successfully redirected to Stripe checkout');
      return {
        success: true,
        subscription: checkoutResult.data,
      };
    } catch (error: any) {
      console.error('Subscription creation error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  // Cancel a subscription
  async cancelSubscription(subscriptionId: string): Promise<SubscriptionResponse> {
    try {
      // Get subscription details
      const { data: subscription, error: fetchError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('id', subscriptionId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      if (!subscription.stripe_subscription_id) {
        throw new Error('No Stripe subscription found');
      }

      // Cancel subscription in Stripe
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: {
          subscriptionId: subscription.stripe_subscription_id,
        },
      });

      if (error) {
        throw error;
      }

      // Update local subscription status
      const { error: updateError } = await supabase
        .from('user_subscriptions')
        .update({ 
          status: 'canceled',
          cancel_at_period_end: true 
        })
        .eq('id', subscriptionId);

      if (updateError) {
        throw updateError;
      }

      return {
        success: true,
        subscription: data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message,
      };
    }
  },

  // Get user's active subscription
  async getActiveSubscription(userId: string): Promise<SubscriptionResponse> {
    try {
      console.log('Fetching active subscription for user:', userId);
      
      const { data: subscription, error } = await supabase
        .from('user_subscriptions')
        .select(`
          *,
          plan:subscription_plans(*)
        `)
        .eq('user_id', userId)
        .eq('status', 'active')
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No subscription found - this is expected for new users
          console.log('No active subscription found for user:', userId);
          return {
            success: true,
            subscription: null,
          };
        } else {
          // Other database error
          console.error('Database error fetching subscription:', error);
          throw error;
        }
      }

      console.log('Active subscription found:', subscription);
      return {
        success: true,
        subscription: subscription,
      };
    } catch (error: any) {
      console.error('Error fetching active subscription:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  // Create customer portal session for subscription management
  async createPortalSession(customerId: string): Promise<SubscriptionResponse> {
    try {
      console.log('Creating portal session for customer:', customerId);
      
      const portalResult = await stripeApi.createPortalSession({
        customerId,
        returnUrl: `${window.location.origin}/dashboard`,
      });

      if (!portalResult.success) {
        // Handle specific error cases
        if (portalResult.error?.includes('Customer not found')) {
          throw new Error('Your subscription information is out of sync. Please contact support to resolve this issue.');
        }
        throw new Error(portalResult.error || 'Failed to create portal session');
      }

      // Redirect to Stripe customer portal
      window.location.href = portalResult.data.url;

      return {
        success: true,
        subscription: portalResult.data,
      };
    } catch (error: any) {
      console.error('Portal session error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  // Check if user can create a new request
  async canCreateRequest(userId: string): Promise<{ canCreate: boolean; reason?: string }> {
    try {
      // Get active subscription
      const subscriptionResult = await this.getActiveSubscription(userId);
      if (!subscriptionResult.success || !subscriptionResult.subscription) {
        return { canCreate: false, reason: 'No active subscription' };
      }

      // Get current active requests count
      const { count: activeRequests } = await supabase
        .from('service_requests')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .in('status', ['pending', 'in_progress', 'review']);

      const maxRequests = subscriptionResult.subscription.plan.max_active_requests;
      
      if ((activeRequests || 0) >= maxRequests) {
        return { canCreate: false, reason: 'Maximum active requests reached' };
      }

      return { canCreate: true };
    } catch (error: any) {
      return { canCreate: false, reason: error.message };
    }
  },

  // Handle successful payment (called from webhook or success page)
  async handleSuccessfulPayment(sessionId: string): Promise<SubscriptionResponse> {
    try {
      console.log('=== PAYMENT HANDLING START ===');
      console.log('Handling successful payment for session:', sessionId);
      
      // Verify the session and get details (Edge Function now handles DB insertion)
      console.log('Calling verify-session Edge Function...');
      const { data, error } = await supabase.functions.invoke('verify-session', {
        body: { sessionId },
      });

      if (error) {
        console.error('Session verification error:', error);
        throw new Error('Failed to verify payment session');
      }

      console.log('Session verified successfully:', data);
      
      // Check if subscription was created
      if (data.newSubscription) {
        console.log('New subscription created:', data.newSubscription);
        return {
          success: true,
          subscription: data.newSubscription,
        };
      } else if (data.existingSubscription) {
        console.log('Subscription already exists:', data.existingSubscription);
        return {
          success: true,
          subscription: data.existingSubscription,
        };
      } else {
        console.error('No subscription data returned from Edge Function');
        throw new Error('Failed to create subscription');
      }
    } catch (error: any) {
      console.error('=== PAYMENT HANDLING ERROR ===');
      console.error('Payment handling error:', error);
      console.error('Error type:', typeof error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  // Debug function to check subscription and customer status
  async debugSubscription(userId: string): Promise<SubscriptionResponse> {
    try {
      // Get user's subscription
      const subscriptionResult = await this.getActiveSubscription(userId);
      
      if (!subscriptionResult.success || !subscriptionResult.subscription) {
        return {
          success: false,
          error: 'No active subscription found',
        };
      }

      const subscription = subscriptionResult.subscription;
      console.log('Current subscription:', subscription);

      // Check if customer exists in Stripe
      if (subscription.stripe_customer_id) {
        try {
          const customerResult = await stripeApi.getCustomer(subscription.stripe_customer_id);
          if (customerResult.success) {
            console.log('Customer exists in Stripe:', customerResult.data);
          } else {
            console.error('Customer not found in Stripe:', customerResult.error);
          }
        } catch (error) {
          console.error('Error checking customer:', error);
        }
      }

      return {
        success: true,
        subscription,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message,
      };
    }
  },
}; 